import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { Company, isServiceTitanPortalV2Company, RoleEnum } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { Divider } from '_lib/components/Divider/Divider';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { Typography } from '_lib/components/Typography/Typography';
import { AuthUser } from '_lib/featureComponents/auth/auth.types';

import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';

import { TabChangeObj } from '../../CompanyUpdate';

import { CompanyConfigDataProviderDisplay } from './CompanyConfigDataProviderDisplay';
import { CompanyConfigDataProviderEdit } from './CompanyConfigDataProviderEdit';

export type CompanyConfigDataProviderProps = {
  company: Company;
  authUser: AuthUser;
  tabChangeObj: TabChangeObj;
};

export const CompanyConfigDataProvider = (props: CompanyConfigDataProviderProps) => {
  const { company, authUser, tabChangeObj } = props;

  const api = useContext(ApiContext);
  const queryClient = useQueryClient();

  const companyDataProviderDeleteMutation = api.companyDataProviders.useDelete();
  const companyQueryKey = api.companies.getByIdQueryKey({ id: company.id });

  const [isEditing, setIsEditing] = useState(false);

  const confirmationDialog = useConfirmationDialog();

  return (
    <>
      {!isEditing && (
        <FlexBox
          sx={(theme) => ({
            backgroundColor: theme.palette.background.default,
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
            position: 'relative',
            padding: 2
          })}>
          <BackdropSpinner open={!!companyDataProviderDeleteMutation.isPending} />
          <FlexCard>
            <PrimaryCardHeader title='Data Provider' />
            {company.dataProvider && (
              <>
                <FlexBox sx={{ padding: 2, overflow: 'auto' }}>
                  <CompanyConfigDataProviderDisplay dataProvider={company.dataProvider} />
                </FlexBox>
                <Divider />
                <FlexBox sx={{ flexDirection: 'row', width: '100%', marginY: 1, paddingX: 1 }}>
                  {(authUser.role === RoleEnum.ADMIN || authUser.role === RoleEnum.SYSTEM) && (
                    <Button
                      variant='contained'
                      color='error'
                      text='Delete'
                      disabled={!company?.isActive}
                      onClick={async () => {
                        const result = await confirmationDialog.show({
                          titleText: 'Delete Data Provider',
                          contentText:
                            'You will no longer be able to access the resources ' +
                            'associated with this provider. It is recommended that this ' +
                            'action be completed during off hours to reduce the impact on ' +
                            'your business. ' +
                            'Do you want to delete this provider?'
                        });
                        if (result?.action === 'CANCEL') return;
                        await companyDataProviderDeleteMutation.deleteItem({ id: company.id });
                        queryClient.invalidateQueries({ queryKey: companyQueryKey });
                      }}
                      sx={{ textTransform: 'none', paddingY: 0.25 }}
                    />
                  )}
                  {isServiceTitanPortalV2Company(company) && (
                    <Button
                      variant='contained'
                      text='Edit'
                      disabled={!company?.isActive}
                      onClick={() => {
                        setIsEditing(true);
                      }}
                      sx={{ marginLeft: 'auto', paddingY: 0.25, textTransform: 'none' }}
                    />
                  )}
                </FlexBox>
              </>
            )}
            {!company.dataProvider && (
              <>
                <Typography text='Data Provider not set' sx={{ padding: 2 }} />
                <Divider />
                <FlexBox sx={{ width: '100%' }}>
                  <Button
                    startIcon={<AddCircleOutlineIcon />}
                    disabled={!company?.isActive}
                    text='Data Provider'
                    variant='contained'
                    sx={{ textTransform: 'none', marginLeft: 'auto', marginY: 1, marginRight: 1 }}
                    onClick={async () => {
                      setIsEditing(true);
                    }}
                  />
                </FlexBox>
              </>
            )}
          </FlexCard>
        </FlexBox>
      )}
      {isEditing && (
        <CompanyConfigDataProviderEdit
          companyDataProvider={isServiceTitanPortalV2Company(company) ? company.dataProvider : null}
          companyId={company.id}
          onClose={() => {
            tabChangeObj.isDirty = false;
            tabChangeObj.isValid = true;
            setIsEditing(false);
          }}
          tabChangeObj={tabChangeObj}
        />
      )}
      {confirmationDialog.element}
    </>
  );
};
